import { useCallback, useEffect, useState } from 'react';
import { KeyRound, Lock } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ConfigPinApi, type ConfigPinStatus } from '@/api/config-pin';
import { errMsg } from '@/api/errors';
import { PinError } from './ConfigPinFields';
import { ConfigPinSetForm } from './ConfigPinSetForm';

interface ConfigPinDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUnlock: () => void;
}

type Mode = 'unlock' | 'change' | 'create' | 'no_pin' | 'locked';

const heure = (iso: string): string =>
  new Date(iso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });

/** Écran d'entrée selon l'état du commerce (mode strict = flag `securite_pin_config`). */
const modeFor = (s: ConfigPinStatus | null): Mode => {
  if (!s || !s.strict) return 'unlock';
  if (s.lockedUntil) return 'locked';
  if (!s.hasPin) return s.canManage ? 'create' : 'no_pin';
  return 'unlock';
};

/**
 * Dialog verrouillant l'accès à la section Configuration.
 * Mode historique : code propre du commerce, sinon code par défaut côté serveur.
 * Mode strict (activable par commerce) : pas de code par défaut, le gérant crée le code,
 * 5 essais faux → blocage 15 min. Super_admin passe toujours (vérifié côté serveur).
 * Session unlock valide tant que l'app n'est pas fermée (pas d'expiration).
 */
export function ConfigPinDialog({ open, onOpenChange, onUnlock }: ConfigPinDialogProps) {
  const [status, setStatus] = useState<ConfigPinStatus | null>(null);
  const [mode, setMode] = useState<Mode>('unlock');
  const [pin, setPin] = useState('');
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    const res = await ConfigPinApi.status();
    const s = res.ok ? res.value : null;
    setStatus(s);
    setMode(modeFor(s));
  }, []);

  useEffect(() => {
    if (!open) return;
    setPin('');
    setError(null);
    void refresh();
  }, [open, refresh]);

  const close = useCallback(() => onOpenChange(false), [onOpenChange]);
  const unlock = useCallback(() => {
    onUnlock();
    close();
  }, [onUnlock, close]);

  const verify = useCallback(
    async (value: string) => {
      setChecking(true);
      setError(null);
      const res = await ConfigPinApi.verify(value);
      setChecking(false);
      if (!res.ok) {
        setError(errMsg(res));
        return;
      }
      if (res.value) {
        unlock();
        return;
      }
      setPin('');
      setError('Code incorrect');
      if (status?.strict) void refresh();
    },
    [unlock, refresh, status?.strict],
  );

  const title =
    mode === 'change'
      ? 'Changer le code'
      : mode === 'create'
        ? 'Créer le code Configuration'
        : 'Accès Configuration';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {mode === 'change' || mode === 'create' ? (
              <KeyRound className="h-4 w-4 text-primary" />
            ) : (
              <Lock className="h-4 w-4 text-amber-500" />
            )}
            {title}
          </DialogTitle>
        </DialogHeader>
        {mode === 'create' || mode === 'change' ? (
          <ConfigPinSetForm
            firstPin={mode === 'create'}
            onDone={mode === 'create' ? unlock : () => setMode('unlock')}
            onBack={mode === 'create' ? close : () => setMode('unlock')}
            onSkip={mode === 'create' ? () => void verify('') : undefined}
          />
        ) : mode === 'no_pin' || mode === 'locked' ? (
          <div className="space-y-3 py-2">
            <p className="text-sm text-muted-foreground">
              {mode === 'locked' && status?.lockedUntil
                ? `Trop d'essais. Réessayez après ${heure(status.lockedUntil)}.`
                : "Le code Configuration n'est pas encore défini. Demandez à votre gérant de le créer."}
            </p>
            <Button variant="outline" className="w-full" onClick={close}>
              Fermer
            </Button>
          </div>
        ) : (
          <div className="space-y-3 py-2">
            <p className="text-sm text-muted-foreground">
              Saisis le code d'accès pour déverrouiller la section Configuration.
            </p>
            <Input
              type="password"
              inputMode="numeric"
              autoFocus
              data-allow-autofocus=""
              placeholder="• • • • •"
              autoComplete="one-time-code"
              aria-label="Code Configuration"
              value={pin}
              onChange={(e) => {
                setPin(e.target.value.replace(/\D/g, ''));
                setError(null);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && pin.length >= 4) void verify(pin);
              }}
              className="h-14 text-center font-mono text-2xl tracking-widest"
              maxLength={10}
            />
            <PinError error={error} />
            <div className="flex gap-2 pt-2">
              <Button variant="outline" onClick={close} className="flex-1" disabled={checking}>
                Annuler
              </Button>
              <Button
                onClick={() => void verify(pin)}
                disabled={checking || pin.length < 4}
                className="navy-gradient flex-1 text-primary-foreground"
              >
                {checking ? 'Vérification…' : 'Déverrouiller'}
              </Button>
            </div>
            <button
              type="button"
              onClick={() => {
                setMode('change');
                setError(null);
              }}
              className="w-full text-center text-xs text-muted-foreground underline hover:text-foreground"
            >
              Changer le code
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
